import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Badge,
    Button,
    Code,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {DnsConnectionFailure} from '../types';
import {providerLabel} from '../providerLabels';

// ⚠️ THE MOST IMPORTANT COMPONENT IN THIS MODULE.
//
// `listDnsZones` fans out across EVERY connection a tenant has. When one of
// them cannot be read, the query still succeeds and returns the zones from the
// connections that worked, plus a `failures` entry for each one that did not.
//
// A tenant with three connections where one is down MUST NOT silently see two
// connections' zones. A shorter list with no explanation is indistinguishable
// from having deleted them — which is the single worst thing this screen could
// do, because the tenant's next move is to go and re-create zones they still
// have.
//
// So this banner is:
//   PERSISTENT   — not a toast. A toast is gone before an operator finishes
//                  reading the table, and the condition it describes is not.
//   NON-BLOCKING — it sits ABOVE the table and the loaded zones render
//                  normally underneath. Partial success is
//                  success-with-a-warning, never an error state.
//   NOT DISMISSIBLE — dismissing it would restore the exact silent-undercount
//                  this exists to prevent. It goes away when the connection
//                  works, and only then.
//
// The provider's `message` travels VERBATIM (FNP-1180: classify for the core,
// preserve for the human). A re-worded message is what made FNP-1087 take a
// whole investigation.

const FailureRow = ({failure}: { failure: DnsConnectionFailure }) => (
    <Stack gap={4}>
        <Group gap={'xs'}>
            <Badge variant={'light'} color={'gray'}>
                {providerLabel(failure.provider)}
            </Badge>
            {/* The normalised class, e.g. AUTHENTICATION_FAILED. Shown next to
                the verbatim message rather than instead of it: the class is
                what an operator scans for, the message is what they act on. */}
            <Code>{failure.kind}</Code>
            {failure.retryable
                ? (
                    <Badge variant={'outline'} color={'yellow'}>
                        {I18n.get('Retryable')}
                    </Badge>
                )
                : (
                    <Badge variant={'outline'} color={'red'}>
                        {I18n.get('Needs attention')}
                    </Badge>
                )}
        </Group>

        {/* VERBATIM. Never re-worded, never truncated into a summary. */}
        <Text size={'sm'}>{failure.message}</Text>

        <Text size={'xs'} c={'dimmed'}>
            {I18n.get('Connection')}: <Code>{failure.connectionId}</Code>
        </Text>

        {/* Retryable and non-retryable need DIFFERENT copy, because they need
            different actions from the tenant. "Try again" on a revoked token
            sends someone into a loop that cannot succeed. */}
        {failure.retryable
            ? (
                <Text size={'xs'} c={'dimmed'}>
                    {I18n.get('This looks temporary — a rate limit or a provider outage. Retrying may work.')}
                </Text>
            )
            : (
                <Text size={'xs'} c={'dimmed'}>
                    {I18n.get('Retrying will not help. The credential for this connection needs to be fixed — most often a revoked or expired token. Reconnect the provider in Settings.')}
                </Text>
            )}
    </Stack>
);

export const ConnectionFailuresAlert = (
    {
        failures,
        onRetry,
        isRetrying = false,
    }: {
        failures: DnsConnectionFailure[],
        onRetry?: () => void,
        isRetrying?: boolean,
    },
) => {

    // `failures` is `[DnsConnectionFailure!]!` — empty, never null. Length is
    // the check, and an empty list renders nothing at all.
    if (!failures || failures.length === 0) {
        return null;
    }

    // A retry is offered only when at least one failure could plausibly succeed
    // on a second attempt. An all-non-retryable banner gets no button, because
    // the button would be a lie.
    const anyRetryable = failures.some(failure => failure.retryable);

    return (
        <Alert
            color={'yellow'}
            variant={'light'}
            title={I18n.get(
                failures.length === 1
                    ? 'One connection could not be read'
                    : 'Some connections could not be read',
            )}
        >
            <Stack gap={'md'}>
                {/* States the undercount EXPLICITLY. Without this sentence an
                    operator has no reason to believe the table is incomplete —
                    they see rows, and rows look like the whole answer. */}
                <Text size={'sm'}>
                    {I18n.get('The zones below are complete for every connection that responded. Zones from the connection(s) listed here are MISSING from this list — they have not been deleted.')}
                </Text>

                {failures.map(failure => (
                    <FailureRow key={failure.connectionId} failure={failure}/>
                ))}

                {anyRetryable && onRetry && (
                    <Group>
                        <Button
                            variant={'outline'}
                            size={'xs'}
                            onClick={onRetry}
                            loading={isRetrying}
                        >
                            {I18n.get('Retry')}
                        </Button>
                    </Group>
                )}
            </Stack>
        </Alert>
    );
};
